import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { getApiErrorMessage } from '@/shared/api/axios';
import { usePayrollSetup, useSaveSettings, type IncomeTaxConfig, type PayrollAccess } from './api';

/** How pay is worked out: statutory deductions on or off, the tax regime and the income-tax slabs. */
export function SettingsTab({ access }: { access: PayrollAccess }) {
  const { data: setup, isLoading } = usePayrollSetup();
  const save = useSaveSettings();
  const [slabs, setSlabs] = useState<IncomeTaxConfig[] | null>(null);
  if (isLoading || !setup) return <p className="p-8 text-center text-on-surface-variant">Loading…</p>;
  const { settings } = setup;
  const editable = access.canFinalize;
  const draft = slabs ?? settings.incomeTaxSlabs;
  const fail = (err: unknown) => toast.error(getApiErrorMessage(err));

  const toggle = async () => {
    const next = !settings.applyStatutoryDeductions;
    const message = next
      ? 'Switch statutory deductions ON?\n\nProvident fund, professional tax and income tax (TDS) will be taken from every payslip generated from now on. Payslips already made are not changed.'
      : 'Switch statutory deductions OFF?\n\nNo provident fund, professional tax or income tax will be taken from payslips generated from now on.';
    if (!window.confirm(message)) return;
    try {
      await save.mutateAsync({ applyStatutoryDeductions: next });
      toast.success(`Statutory deductions are now ${next ? 'on' : 'off'}.`);
    } catch (err) { fail(err); }
  };

  const setConfig = (regime: 'old' | 'new', patch: Partial<IncomeTaxConfig>) => setSlabs(draft.map((c) => (c.regime === regime ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-8 p-6">
      <section className="rounded-lg border border-outline p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="max-w-xl">
            <h3 className="font-semibold">Statutory deductions</h3>
            <p className="mt-1 text-sm text-on-surface-variant">Provident fund, professional tax and income tax (TDS). They are <strong>{settings.applyStatutoryDeductions ? 'on' : 'off'}</strong>. Switch them on if the company starts paying them: the components are already set up under Salary setup, and the income-tax slabs below are used for TDS.</p>
          </div>
          {editable ? (
            <Button variant={settings.applyStatutoryDeductions ? 'outline' : 'primary'} onClick={toggle} disabled={save.isPending}>{settings.applyStatutoryDeductions ? 'Switch off' : 'Switch on'}</Button>
          ) : <span className="text-sm text-on-surface-variant">Needs payroll approval rights to change.</span>}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div><h3 className="font-semibold">Income tax (used only when statutory deductions are on)</h3><p className="text-xs text-on-surface-variant">Tax is worked out on the year's projected pay and shared over 12 months.</p></div>
          <div className="w-48">
            <Select label="Tax regime" value={settings.taxRegime} disabled={!editable} options={[{ value: 'new', label: 'New regime' }, { value: 'old', label: 'Old regime' }]}
              onChange={(e) => save.mutateAsync({ taxRegime: e.target.value }).then(() => toast.success('Regime changed.')).catch(fail)} />
          </div>
        </div>
        {draft.map((config) => (
          <div key={config.regime} className="space-y-2 rounded-lg border border-outline p-3">
            <h4 className="font-medium capitalize">{config.regime} regime</h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Input label="Standard deduction (₹)" type="number" min={0} disabled={!editable} value={config.standardDeduction} onChange={(e) => setConfig(config.regime, { standardDeduction: Number(e.target.value) })} />
              <Input label="Cess (%)" type="number" min={0} max={100} disabled={!editable} value={config.cessPercent} onChange={(e) => setConfig(config.regime, { cessPercent: Number(e.target.value) })} />
              <Input label="No tax up to taxable income (₹)" type="number" min={0} disabled={!editable} value={config.rebateThreshold} onChange={(e) => setConfig(config.regime, { rebateThreshold: Number(e.target.value) })} />
            </div>
            <div className="space-y-1">
              {config.slabs.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input type="number" min={0} disabled={!editable} placeholder="Income from (₹)" value={s.minAnnualIncome} onChange={(e) => setConfig(config.regime, { slabs: config.slabs.map((x, j) => (j === i ? { ...x, minAnnualIncome: Number(e.target.value) } : x)) })} />
                  <Input type="number" min={0} max={100} disabled={!editable} className="w-28" placeholder="Rate %" value={s.rate} onChange={(e) => setConfig(config.regime, { slabs: config.slabs.map((x, j) => (j === i ? { ...x, rate: Number(e.target.value) } : x)) })} />
                  {editable && <Button size="sm" variant="ghost" onClick={() => setConfig(config.regime, { slabs: config.slabs.filter((_, j) => j !== i) })}>Remove</Button>}
                </div>
              ))}
              {editable && <Button size="sm" variant="ghost" onClick={() => setConfig(config.regime, { slabs: [...config.slabs, { minAnnualIncome: 0, rate: 0 }] })}><Plus className="mr-1 h-3.5 w-3.5" /> Add slab</Button>}
            </div>
          </div>
        ))}
        {editable && slabs && (
          <div className="flex gap-2">
            <Button onClick={() => save.mutateAsync({ incomeTaxSlabs: slabs }).then(() => { toast.success('Slabs saved.'); setSlabs(null); }).catch(fail)} disabled={save.isPending}>Save slabs</Button>
            <Button variant="ghost" onClick={() => setSlabs(null)}>Discard</Button>
          </div>
        )}
      </section>
    </div>
  );
}
